import { ImageScaleView, MovieClip } from '@play-co/timestep-core/ui';
import { Container, clamp } from '../../../odie';
import { ColorId, FirecrackerBoxBlockProps, colorIds } from '../../defs/block';
import { blockPositionView } from '../../util/blockTools';
import { getMatch2AssetPath } from '../../util/assets';
import { getBlockViewScale } from './FitWidthBlockSprite';
import { waitForItPromise } from 'src/lib/utils';

export type FirecrackerBoxColor = Exclude<ColorId, 'all'>;

/*
 * Firecracker Box block view.
 */
export class FirecrackerBoxBlockView extends Container {
  private movieclip: MovieClip;
  private _color: FirecrackerBoxColor = 'blue';
  private _count: number = 0;

  public get color(): FirecrackerBoxColor {
    return this._color;
  }

  public set color(value: FirecrackerBoxColor) {
    if (this._color === value) return;

    // Apply new color substitutions.
    this.applyNewColorSubstitutions(value);

    // Play color switch animation.
    this.movieclip.play(`colorswitch_stage${this.getAnimationStage()}`);

    // update field
    this._color = value;
  }

  public get count(): number {
    return this._count;
  }

  public set count(value: number) {
    this._count = value;

    // Make sure current color is correct.
    this.applyColorSubstitutions(this._color);
    this.applyNewColorSubstitutions(this._color);

    this.movieclip.play(`colorswitch_stage${this.getAnimationStage()}`);
    this.movieclip.pause();
  }

  public getAnimationStage() {
    return 7 - clamp(this.count, 0, 7);
  }

  constructor(props: FirecrackerBoxBlockProps) {
    // create base view
    super();

    this.view.updateOpts({
      scale: getBlockViewScale(264, props),
    });

    this.movieclip = new MovieClip({
      superview: this.view,
      url: FirecrackerBoxBlockView.getMovieclipPath(),
      offsetY: 5,
      fps: 40,
    });

    // position
    blockPositionView(this, props);

    // set fields
    this._color = (props.color as FirecrackerBoxColor) ?? 'green';

    // Make sure current color is correct.
    this.applyColorSubstitutions(this._color);
    this.applyNewColorSubstitutions(this._color);

    this._count = props.count ?? 7;
    this.movieclip.play(`shake_stage${this.getAnimationStage()}`);
    this.movieclip.pause();
  }

  private applyColorSubstitutions(color: FirecrackerBoxColor) {
    const substitutions = FirecrackerBoxBlockView.getSubstitutionPath(color);
    Object.entries(substitutions).forEach(([key, value]) => {
      this.movieclip.addViewSubstitution(
        key,
        new ImageScaleView({
          ...value,
          centerOnOrigin: true,
        }),
      );
    });
  }

  private applyNewColorSubstitutions(newColor: FirecrackerBoxColor) {
    const newSubstitutions =
      FirecrackerBoxBlockView.getSubstitutionPath(newColor);
    this.movieclip.addViewSubstitution(
      'ph_frame_nextcolor',
      new ImageScaleView({
        ...newSubstitutions.ph_frame_currentcolor,
        centerOnOrigin: true,
      }),
    );
    this.movieclip.addViewSubstitution(
      'ph_rocket_nextcolor',
      new ImageScaleView({
        ...newSubstitutions.ph_rocket_currentcolor,
        centerOnOrigin: true,
      }),
    );
  }

  public shake() {
    this.applyColorSubstitutions(this._color);
    return this.movieclip.playAsync(`shake_stage${this.getAnimationStage()}`);
  }

  public async launch() {
    this._count--;
    this.applyColorSubstitutions(this._color);

    // Stop stage animations prematurely.
    await Promise.race([
      this.movieclip.playAsync('fireworks_stage' + this.getAnimationStage()),
      waitForItPromise(this.getAnimationStage() !== 7 ? 900 : 2000),
    ]);
  }

  public static assets() {
    return [
      {
        url: this.getMovieclipPath(),
        loadMethod: MovieClip.animationLoader,
      },
      ...colorIds
        .filter((color) => color !== 'all')
        .map((color) =>
          Object.values(
            this.getSubstitutionPath(color as FirecrackerBoxColor),
          ).map((path) => path.image),
        )
        .flat(),
    ];
  }

  public static getMovieclipPath() {
    return getMatch2AssetPath('blocks/firecrackerBox/clip');
  }

  private static getSubstitutionPath(color: FirecrackerBoxColor) {
    return {
      ph_frame_currentcolor: {
        image: getMatch2AssetPath(
          `firecrackerBox/substitutions/block.firecracker_frame_${color}.png`,
        ),
        width: 264,
        height: 264,
      },
      ph_rocket_currentcolor: {
        image: getMatch2AssetPath(
          `firecrackerBox/substitutions/block.firecracker_${color}.png`,
        ),
        width: 115,
        height: 205,
        scale: 0.7,
      },
      ph_firespark: {
        image: getMatch2AssetPath(
          `firecrackerBox/substitutions/block.firework_spark_${color}.png`,
        ),
        width: 50,
        height: 50,
      },
      ph_firestreak: {
        image: getMatch2AssetPath(
          `firecrackerBox/substitutions/block.firework_streak_${color}.png`,
        ),
        width: 20,
        height: 140,
      },
      ph_fireworkring: {
        image: getMatch2AssetPath(
          `firecrackerBox/substitutions/block.firework_ring_${color}.png`,
        ),
        width: 200,
        height: 200,
      },
    };
  }
}
